import { useMemo, useState } from "react";
import { gbp } from "@/lib/api";
import { useSite } from "@/lib/site";

const PLACEHOLDER_RATE = 0.099;

export default function FinancePanel({ price, monthly }) {
  const site = useSite();
  const [deposit, setDeposit] = useState(Math.round((price || 0) * 0.15 / 500) * 500);
  const [term, setTerm] = useState(48);
  const est = useMemo(() => {
    const p = Math.max(0, (price || 0) - deposit);
    const r = PLACEHOLDER_RATE / 12;
    return p ? Math.round((p * r) / (1 - Math.pow(1 + r, -term))) : 0;
  }, [price, deposit, term]);
  if (!price) return null;
  return (
    <div className="border border-stone bg-[#FBF9F4] p-6 sm:p-10" data-testid="finance-panel">
      <p className="eyebrow text-forest">Finance</p>
      <h2 className="mt-3 font-serif text-4xl text-charcoal">Flexible finance options</h2>
      {monthly && <p className="mt-2 text-charcoal/80">Indicatively from <strong className="text-forest">{gbp(monthly)} per month</strong>.</p>}
      <div className="mt-8 grid gap-8 md:grid-cols-2">
        <div className="space-y-6">
          <div>
            <label htmlFor="fin-deposit" className="flex justify-between text-sm font-semibold"><span>Deposit</span><span data-testid="finance-deposit-value">{gbp(deposit)}</span></label>
            <input id="fin-deposit" type="range" min={0} max={Math.round(price * 0.5)} step={500} value={deposit} onChange={(e) => setDeposit(+e.target.value)} className="mt-3 w-full accent-[#16352C]" data-testid="finance-deposit" />
          </div>
          <div>
            <p className="text-sm font-semibold">Term</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {[24, 36, 48, 60].map((t) => (
                <button key={t} type="button" onClick={() => setTerm(t)} data-testid={`finance-term-${t}`}
                  className={`border px-4 py-2 text-sm transition-colors ${term === t ? "border-forest bg-forest text-paper" : "border-stone-dark hover:border-forest"}`}>{t} months</button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-col justify-center bg-forest p-6 text-paper">
          <p className="text-xs uppercase tracking-[0.16em] text-stone">Illustrative monthly payment</p>
          <p className="mt-2 font-serif text-5xl" data-testid="finance-estimate">{gbp(est)}</p>
          <p className="mt-2 text-xs text-stone">Based on a placeholder rate for illustration only.</p>
        </div>
      </div>
      <p className="mt-6 text-xs leading-relaxed text-charcoal/75">{site.finance_apr_note || "Finance figures are illustrative until connected to an approved finance provider."} No finance application can be made through this website at present.</p>
    </div>
  );
}
